import { NavLink, useLocation } from "react-router-dom";
import { ContactLink } from "./ContactLink";
import {
  BENCHMARK_DESIGN_MODE,
  BENCHMARK_NAV_UNLOCKED_PATHS,
} from "../config/benchmarkDesignMode";
import { navigationItems } from "../config/navigation";
import { useAuth } from "../context/AuthContext";
import { isNavigationItemUnavailable } from "../services/navigationAccess";

/*
 * OPENAPPCART LOCKED RIGHT NAVIGATION
 * Shared across all public pages via AppShellChrome / PublicLayout.
 * Only active route highlighting may change at runtime.
 * Styles: Frontend/src/styles/shared-shell.css
 */

type SideNavigationProps = {
  onNavigate?: () => void;
};

export function SideNavigation({ onNavigate }: SideNavigationProps) {
  const { user } = useAuth();
  const location = useLocation();
  const publisherDashboardContext =
    (location.state as { publisherDashboardContext?: boolean } | null)
      ?.publisherDashboardContext === true;
  const isPublisherDashboardSectionActive =
    location.pathname === "/publisher-dashboard" ||
    location.pathname.startsWith("/publisher/apps/") ||
    /^\/publish-your-app\/[^/]+\/edit$/.test(location.pathname) ||
    (location.pathname.startsWith("/apps/") && publisherDashboardContext);
  const isDiscoverAppsSectionActive =
    (location.pathname === "/discover-apps" ||
      location.pathname.startsWith("/apps/")) &&
    !isPublisherDashboardSectionActive;

  return (
    <aside
      aria-label="Primary navigation"
      className="oac-side-navigation"
    >
      <nav className="oac-side-navigation__list">
        {navigationItems.map((item) => {
          const unavailable = isNavigationItemUnavailable(item, user, {
            benchmarkDesignMode: BENCHMARK_DESIGN_MODE,
            benchmarkUnlockedPaths: BENCHMARK_NAV_UNLOCKED_PATHS,
          });

          if (unavailable) {
            return (
              <span
                aria-disabled="true"
                className="oac-side-navigation__link oac-side-navigation__link--muted oac-side-navigation__link--disabled"
                key={item.path}
              >
                {item.label}
              </span>
            );
          }

          return (
            <NavLink
              className={({ isActive }) => {
                const active =
                  item.path === "/discover-apps"
                    ? isDiscoverAppsSectionActive
                    : item.path === "/publisher-dashboard"
                      ? isPublisherDashboardSectionActive
                      : item.path === "/publish-your-app"
                        ? location.pathname === "/publish-your-app"
                        : isActive;

                return [
                  "oac-side-navigation__link",
                  active ? "oac-side-navigation__link--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
              }}
              key={item.path}
              onClick={onNavigate}
              to={item.path}
            >
              {item.label}
            </NavLink>
          );
        })}

        <ContactLink onNavigate={onNavigate} />
      </nav>
    </aside>
  );
}
